import { Invoice } from '@/renderer/types/billing';
import { ReceiptText, Sparkles } from 'lucide-react';
import React from 'react';

import { BillingMetricPill } from './BillingMetricPill';
import { BillingStatusBadge } from './BillingStatusBadge';
import { BillingSurface } from './BillingSurface';
import { formatBillingDate, formatMoney } from './formatters';

interface BillingInvoiceSummaryProps {
  invoices: Invoice[];
  latestPaidInvoice: Invoice | null;
  upcomingInvoice: Invoice | null;
}

export const BillingInvoiceSummary: React.FC<BillingInvoiceSummaryProps> = ({
  invoices,
  latestPaidInvoice,
  upcomingInvoice,
}) => {
  return (
    <BillingSurface className="h-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Invoice Snapshot
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Incoming invoices are synced locally so billing history stays
            available inside the app.
          </p>
        </div>
        <BillingStatusBadge
          icon={<Sparkles size={12} />}
          label={invoices.length > 0 ? 'Synced' : 'Awaiting first invoice'}
          tone={invoices.length > 0 ? 'success' : 'neutral'}
        />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <BillingMetricPill
          label="Upcoming invoice"
          value={
            upcomingInvoice
              ? formatMoney(upcomingInvoice.amount_due, {
                  currency: upcomingInvoice.currency.toUpperCase(),
                  fromMinorUnits: true,
                })
              : 'No invoice yet'
          }
          hint={
            upcomingInvoice
              ? `Due ${formatBillingDate(
                  upcomingInvoice.due_date || upcomingInvoice.invoice_date,
                )}`
              : 'Stripe will create the next billing record automatically.'
          }
          icon={<ReceiptText size={16} />}
        />
        <BillingMetricPill
          label="Latest paid"
          value={
            latestPaidInvoice
              ? formatMoney(latestPaidInvoice.amount_paid, {
                  currency: latestPaidInvoice.currency.toUpperCase(),
                  fromMinorUnits: true,
                })
              : 'No paid invoice yet'
          }
          hint={
            latestPaidInvoice
              ? formatBillingDate(
                  latestPaidInvoice.paid_at || latestPaidInvoice.invoice_date,
                )
              : `${invoices.length} invoice${
                  invoices.length === 1 ? '' : 's'
                } tracked so far`
          }
          icon={<Sparkles size={16} />}
        />
      </div>
    </BillingSurface>
  );
};
